import { Link, useRouter } from "expo-router";
import { useMemo } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { fontFamily, space } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { ScreenAppbar } from "../src/components/screen-appbar";
import { ActionTiles } from "../src/home/components/action-tiles";
import { MasteryList } from "../src/home/components/mastery-list";
import { ReadyCard } from "../src/home/components/ready-card";
import { useHomeData } from "../src/home/use-home-data";
import { useReadiness } from "../src/home/use-readiness";
import { TabScaffold } from "../src/tab-scaffold";
import { type Colors, useColors } from "../src/theme";
import { useEffectiveSession } from "../src/use-effective-session";
import { useResponsive } from "../src/use-responsive";

// Derives a friendly greeting name from an email when no display name
// exists. Takes the local-part, drops plus-tags and anything from the
// first digit/separator on, then title-cases the leading name token —
// so "ada.lovelace+test99@x" greets as "Ada", not "ada.lovelace+test99".
// Falls back to "there" when nothing name-like remains.
function friendlyFirstName(email: string | null | undefined): string {
  const local = email?.split("@")[0];
  if (!local) return "there";
  const token = local
    .split("+")[0]
    ?.split(/[._\-0-9]/)
    .find((part) => part.length > 0);
  if (!token) return "there";
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

// Paid home dashboard. Orchestrates the readiness card, action tiles,
// and mastery list — data flows through useHomeData (fetch + appbar
// resolution) and useReadiness (mastery → pct / verdict / actionable
// copy). TabScaffold supplies the navigation chrome: a bottom tab bar
// on native/narrow, a left sidebar on web/wide (design_handoff_2 Rule B).
export default function Home(): JSX.Element {
  const router = useRouter();
  const session = useEffectiveSession();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();

  const home = useHomeData();
  const r = useReadiness(home.mastery);
  // Weakest topic drives the web "Focus next" card (design_handoff_2 §3).
  const focus =
    r.masteryTop.length > 0
      ? r.masteryTop.reduce((lo, row) =>
          (row.recentAccuracy ?? 1) < (lo.recentAccuracy ?? 1) ? row : lo,
        )
      : null;

  const hour = new Date().getHours();
  const partOfDay =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = friendlyFirstName(session?.user?.email);

  if (home.noVerticalSelected) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.error}>No credential selected.</Text>
        <Link href="/welcome" asChild>
          <Button label="Pick a credential" fullWidth />
        </Link>
      </View>
    );
  }
  if (home.mastery === null && home.error === null) {
    return (
      <View style={styles.errorContainer}>
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }
  // Mastery fetch failed and we have no data to show. Render a friendly
  // error + retry instead of the dashboard, which would otherwise show a
  // fabricated "0% / Just getting started" (#11).
  if (home.error !== null && home.mastery === null) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.error}>{home.error}</Text>
        <Button
          label="Try again"
          onPress={() => router.replace("/home")}
          accessibilityLabel="Retry loading your dashboard"
          fullWidth
        />
      </View>
    );
  }

  const greeting = (
    <View style={styles.greetBlock}>
      <Text style={styles.greet}>
        {partOfDay}, {firstName}
      </Text>
      <Text style={styles.greetHead}>Let&apos;s close the gaps.</Text>
    </View>
  );

  // Wide (web): greeting row with a Resume CTA, then a two-column grid —
  // readiness + mastery on the left, action tiles on the right.
  const body = isWide ? (
    <ScrollView contentContainerStyle={styles.wideScroll}>
      <View style={styles.wideHeaderRow}>
        {greeting}
        <Button
          label="Resume practice →"
          onPress={() => router.push("/practice")}
          accessibilityLabel="Resume practice"
        />
      </View>
      <View style={styles.wideGrid}>
        <View style={styles.wideColMain}>
          <ReadyCard pct={r.pct} title={r.verdictCopy} sub={r.readySub} large />
          <MasteryList rows={r.masteryTop} />
        </View>
        <View style={styles.wideColSide}>
          <ActionTiles
            onSmartPractice={() => router.push("/practice")}
            onVoiceCoach={() => router.push("/voice")}
          />
          {focus ? (
            <View style={styles.focusCard}>
              <Text style={styles.focusEyebrow}>FOCUS NEXT</Text>
              <Text style={styles.focusLabel}>{focus.label}</Text>
              <Text style={styles.focusPct}>
                {Math.round((focus.recentAccuracy ?? 0) * 100)}% — your weakest
                topic right now.
              </Text>
            </View>
          ) : null}
        </View>
      </View>
      {home.error ? <Text style={styles.error}>{home.error}</Text> : null}
    </ScrollView>
  ) : (
    <View style={styles.outer}>
      <ScreenAppbar title={home.appbarTitle} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {greeting}
        <ReadyCard pct={r.pct} title={r.verdictCopy} sub={r.readySub} />
        <ActionTiles
          onSmartPractice={() => router.push("/practice")}
          onVoiceCoach={() => router.push("/voice")}
        />
        <MasteryList rows={r.masteryTop} />
        {home.error ? <Text style={styles.error}>{home.error}</Text> : null}
      </ScrollView>
    </View>
  );

  return (
    <TabScaffold
      userLabel={session?.user?.email ?? "Account"}
      metaLabel={home.appbarTitle}
    >
      {body}
    </TabScaffold>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    outer: { flex: 1, backgroundColor: colors.background },
    errorContainer: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
      gap: space[4],
    },
    scrollContent: {
      padding: space[6],
      paddingTop: space[6],
      paddingBottom: space[12],
      maxWidth: 520,
      alignSelf: "center",
      width: "100%",
      gap: space[4],
    },
    wideScroll: {
      padding: space[10],
      paddingBottom: space[12],
      maxWidth: 1040,
      width: "100%",
      alignSelf: "center",
      gap: space[8],
    },
    wideHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: space[6],
    },
    wideGrid: {
      flexDirection: "row",
      gap: space[6],
      alignItems: "flex-start",
    },
    wideColMain: { flex: 1.55, gap: space[5] },
    wideColSide: { flex: 1, gap: space[4] },
    focusCard: {
      backgroundColor: colors.surfaceCaution,
      borderWidth: 1,
      borderColor: colors.caution,
      borderRadius: 15,
      padding: 16,
      gap: 4,
    },
    focusEyebrow: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 11,
      letterSpacing: 1.2,
      color: colors.caution,
    },
    focusLabel: {
      fontFamily: fontFamily.bold,
      fontSize: 16,
      color: colors.textPrimary,
    },
    focusPct: {
      fontFamily: fontFamily.regular,
      fontSize: 13,
      lineHeight: 18,
      color: colors.textSecondary,
    },
    greetBlock: { gap: 2 },
    greet: {
      fontFamily: fontFamily.regular,
      fontSize: 15,
      color: colors.textMuted,
    },
    greetHead: {
      fontFamily: fontFamily.extraBold,
      fontSize: 28,
      lineHeight: 32,
      letterSpacing: -0.4,
      color: colors.heading,
    },
    error: {
      fontFamily: fontFamily.regular,
      fontSize: 17,
      color: colors.error,
      textAlign: "center",
    },
  });
}
